import { motion } from "framer-motion";
import { useMemo } from "react";
import type { Car, FuelActivityFilters, FuelLog, GasStation, GasStationBrand, User } from "@/types/database";
import {
  buildFuelInsights,
  buildFuelUserSummaries,
  buildYearlySpendInsight,
  enrichFuelLogs,
  filterEnrichedLogs,
  filterFuelUserSummaries,
  filterLogsByDateRange,
  getCurrentMonthFuelOverview,
  getFuelActivityCharts,
  getFuelPopularityMetrics,
  getYearlySpendBreakdown,
  previousPeriodLogs,
  sortFuelUserSummaries,
  type FuelInsight,
} from "@/lib/fuel-analytics";
import { exportFuelInsightsJson, exportFuelLogsCsv, exportFuelUserSummariesCsv } from "@/lib/fuel-export";
import { PlatformFuelCharts } from "@/components/fuel-activity/FuelActivityCharts";
import { FuelActivityFiltersBar } from "@/components/fuel-activity/FuelActivityFiltersBar";
import { FuelCurrentMonthMetrics } from "@/components/fuel-activity/FuelCurrentMonthMetrics";
import { FuelInsightsPanel } from "@/components/fuel-activity/FuelInsightsPanel";
import { FuelUserExplorer } from "@/components/fuel-activity/FuelUserExplorer";
import { MetricCard } from "@/components/ui/Card";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";
import { formatNumber } from "@/lib/utils";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
  },
};

type Props = {
  logs: FuelLog[];
  users: User[];
  cars: Car[];
  stations: GasStation[];
  brands: GasStationBrand[];
  filters: FuelActivityFilters;
  onFiltersChange: (next: FuelActivityFilters) => void;
};

export function FuelActivityAnalytics({
  logs,
  users,
  cars,
  stations,
  brands,
  filters,
  onFiltersChange,
}: Props) {
  const periodLogs = useMemo(
    () => filterLogsByDateRange(logs, filters.dateRange),
    [logs, filters.dateRange]
  );

  const currentMonth = useMemo(() => getCurrentMonthFuelOverview(logs), [logs]);
  const charts = useMemo(() => getFuelActivityCharts(periodLogs, users), [periodLogs, users]);
  const previous = useMemo(
    () => previousPeriodLogs(logs, filters.dateRange),
    [logs, filters.dateRange]
  );
  const insights = useMemo(() => buildFuelInsights(periodLogs, previous), [periodLogs, previous]);

  const yearlyBreakdown = useMemo(
    () => getYearlySpendBreakdown(logs, users, cars, stations, new Date().getFullYear()),
    [logs, users, cars, stations]
  );

  const yearlySpendInsight = useMemo(
    () => buildYearlySpendInsight(yearlyBreakdown),
    [yearlyBreakdown]
  );

  const popularity = useMemo(
    () => getFuelPopularityMetrics(periodLogs, stations, brands),
    [periodLogs, stations, brands]
  );

  const popularityInsights = useMemo((): FuelInsight[] => {
    const items: FuelInsight[] = [];
    if (popularity.topDayOfWeek) {
      items.push({
        id: "popular-day",
        tone: "neutral",
        title: `${popularity.topDayOfWeek.day} is the busiest fill-up day`,
        detail: `${formatNumber(popularity.topDayOfWeek.fillUps)} fill-ups (${popularity.topDayOfWeek.sharePercent}% of all logs in this period).`,
      });
    }
    if (popularity.topBrand) {
      items.push({
        id: "popular-brand",
        tone: "neutral",
        title: `${popularity.topBrand.brandName} is the most-used brand`,
        detail: `${formatNumber(popularity.topBrand.fillUps)} fill-ups (${popularity.topBrand.sharePercent}% of logs).`,
      });
    }
    if (popularity.topStation) {
      items.push({
        id: "popular-station",
        tone: "neutral",
        title: `${popularity.topStation.name} sees the most fill-ups`,
        detail: `${popularity.topStation.address} · ${formatNumber(popularity.topStation.fillUps)} visits (${popularity.topStation.sharePercent}%).`,
      });
    }
    return items;
  }, [popularity]);

  const allInsights = useMemo(() => {
    const items: FuelInsight[] = [];
    if (yearlySpendInsight) items.push(yearlySpendInsight);
    items.push(...popularityInsights, ...insights);
    return items;
  }, [yearlySpendInsight, popularityInsights, insights]);

  const summaries = useMemo(() => {
    const built = buildFuelUserSummaries(periodLogs, users);
    const filtered = filterFuelUserSummaries(built, filters);
    return sortFuelUserSummaries(filtered, filters.sort);
  }, [periodLogs, users, filters]);

  const enrichedLogs = useMemo(() => {
    const enriched = enrichFuelLogs(periodLogs, users, cars, stations);
    return filterEnrichedLogs(enriched, filters);
  }, [periodLogs, users, cars, stations, filters]);

  return (
    <div className="space-y-6">
      <Workspace>
        <WorkspaceSection className="border-b border-border/30 p-4 sm:p-5">
          <FuelActivityFiltersBar
            filters={filters}
            onChange={onFiltersChange}
            onExport={() => exportFuelUserSummariesCsv(summaries, "fuel-user-summaries.csv")}
            exportLabel="Export users CSV"
          />
        </WorkspaceSection>
      </Workspace>

      <motion.div variants={containerVariants} initial="hidden" animate="show">
        <motion.div variants={itemVariants}>
          <FuelCurrentMonthMetrics
            monthLabel={currentMonth.label}
            overview={currentMonth.overview}
            spendLink="/fuel-activity/spend"
          />
        </motion.div>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
      >
        <motion.div variants={itemVariants}>
          <MetricCard
            label="Busiest day"
            value={popularity.topDayOfWeek?.day ?? "—"}
            detail={
              popularity.topDayOfWeek
                ? `${formatNumber(popularity.topDayOfWeek.fillUps)} fill-ups · ${popularity.topDayOfWeek.sharePercent}% of period`
                : "No fill-up data yet"
            }
          />
        </motion.div>
        <motion.div variants={itemVariants}>
          <MetricCard
            label="Top brand"
            value={popularity.topBrand?.brandName ?? "—"}
            detail={
              popularity.topBrand
                ? `${formatNumber(popularity.topBrand.fillUps)} fill-ups · ${popularity.topBrand.sharePercent}% of period`
                : "No brand data yet"
            }
          />
        </motion.div>
        <motion.div variants={itemVariants}>
          <MetricCard
            label="Top station"
            value={popularity.topStation?.name ?? "—"}
            detail={
              popularity.topStation
                ? `${popularity.topStation.address} · ${formatNumber(popularity.topStation.fillUps)} visits`
                : "No station data yet"
            }
          />
        </motion.div>
      </motion.div>

      <FuelInsightsPanel insights={allInsights} />

      <PlatformFuelCharts charts={charts} />

      <Workspace>
        <WorkspaceSection className="border-b border-border/30 p-4 sm:p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted">User breakdown</p>
              <p className="mt-0.5 text-sm text-muted">
                Click any user to open their full fuel activity profile.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => exportFuelLogsCsv(enrichedLogs, "fuel-logs.csv")}
                className="rounded-full bg-canvas px-4 py-2 text-sm font-semibold transition hover:bg-canvas-hover"
              >
                Export logs CSV
              </button>
              <button
                type="button"
                onClick={() =>
                  exportFuelInsightsJson(
                    yearlyBreakdown.overview,
                    allInsights,
                    "fuel-insights.json",
                    popularity,
                    yearlyBreakdown
                  )
                }
                className="rounded-full bg-canvas px-4 py-2 text-sm font-semibold transition hover:bg-canvas-hover"
              >
                Export insights JSON
              </button>
            </div>
          </div>
          <FuelUserExplorer summaries={summaries} stations={stations} />
        </WorkspaceSection>
      </Workspace>
    </div>
  );
}
